import React, { createContext, useContext, useEffect, useRef } from "react";
import { devtools } from "valtio/utils";

import {
  QuizCtx,
  QuizSlideState,
  QuizState,
  SelectorState,
  createQuizState,
} from "../internal/state";
import { QuizErrorEvent, TrackingEventCallback } from "./types";

export type QuizProps = {
  children?: React.ReactNode;
} & QuizConfigType;

export type QuizConfigType = {
  locationApiKey?: string;
  initialSlideStateByID?: Record<string, SelectorState>;
  onErrorEvent?: (event: QuizErrorEvent) => void;
  // return only the id for now to not expose the proxied objects
  onSlideChange?: (newSlide: { id: string }) => void;
  onSlideSubmitted?: (state: {
    id: string;
    state: SelectorState;
    getQuizState: () => Promise<QuizSlideState>;
  }) => void;
  onTrackingEvent?: TrackingEventCallback;
  onQuizLoaded?: (state: QuizState) => void;
  enableSlideTransition?: boolean;
  slideTransitionInDuration?: number;
  slideTransitionOutDuration?: number;
};

const QuizConfigCtx = createContext<QuizConfigType>(null as any);

export function useQuizConfig() {
  return useContext(QuizConfigCtx);
}

function getCurrentSlideID() {
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    return params.get("slideid") ?? undefined;
  }
}

export function QuizProvider(props: QuizProps) {
  const { children, initialSlideStateByID, ...rest } = props;
  const { onSlideSubmitted, onTrackingEvent, onQuizLoaded } = rest;

  const q = useRef(
    (function () {
      return createQuizState({
        initialState: {
          currentSlideID: getCurrentSlideID(),
          slideStateByID: initialSlideStateByID,
        },
        onSlideSubmitted,
        onTrackingEvent,
      });
    })()
  ).current;

  const { state } = q;

  useEffect(() => {
    devtools(state, {});
  }, []);

  useEffect(() => {
    onQuizLoaded?.(q);
  }, []);

  return (
    <QuizConfigCtx.Provider value={{ ...rest }}>
      <QuizCtx.Provider value={q}>{children}</QuizCtx.Provider>
    </QuizConfigCtx.Provider>
  );
}
